import { useContext, useEffect, useMemo } from 'react';
import { ServiceReportExtended } from '@sdir/httpclient/lib/clients/aps/seagoing';
import { ServiceReportQualificationResult } from '@sdir/httpclient/lib/clients/aps/application';
import { Loadable } from '../../../types';
import { AddedQualificationsContext } from '../../contexts';
import { isExternalUser } from '../../../helpers/Qualifications';

type UseAddedQualificationsParams = {
  seagoing?: boolean;
  newSeagoing?: boolean;
  all?: boolean;
  group?: string;
};

type UseAddedQualificationsResult = {
  addedSeagoing: Loadable<ServiceReportExtended[]>;
  addedNewSeagoing: Loadable<ServiceReportQualificationResult>;
  refreshSeagoing: () => void;
  refreshNewSeagoing: () => void;
};

const isInitialized = (loadable: Loadable) =>
  !!loadable.loading || !!loadable.error || loadable.result !== undefined;

export const useAddedQualifications = ({
  seagoing: loadSeagoing,
  newSeagoing: loadNewSeagoing,
  all: loadAll,
  group: groupFilter,
}: UseAddedQualificationsParams): UseAddedQualificationsResult => {
  const { isReady, addedSeagoing, addedNewSeagoing, loadAddedSeagoing, loadNewAddedSeagoing } =
    useContext(AddedQualificationsContext);

  const isExternal = isExternalUser();

  const shouldLoadSeagoing = loadSeagoing || loadAll;
  const shouldLoadNewSeagoing = loadNewSeagoing || loadAll;

  const filteredSeagoing = useMemo((): Loadable<ServiceReportExtended[]> => {
    if (shouldLoadSeagoing && !isReady) return { loading: true };
    if (!groupFilter || !addedSeagoing.result) return addedSeagoing;
    return { result: addedSeagoing.result.filter((x) => x.applicationGroupUid === groupFilter) };
  }, [addedSeagoing, groupFilter, isReady, shouldLoadSeagoing]);

  const result = useMemo(
    () => ({
      addedSeagoing: filteredSeagoing,
      addedNewSeagoing,
      refreshSeagoing: loadAddedSeagoing,
      refreshNewSeagoing: loadNewAddedSeagoing,
    }),
    [filteredSeagoing, addedNewSeagoing, loadAddedSeagoing, loadNewAddedSeagoing]
  );

  useEffect(() => {
    if (!isExternal && isReady && shouldLoadSeagoing && !isInitialized(addedSeagoing)) {
      loadAddedSeagoing();
    }
    if (!isExternal && isReady && shouldLoadNewSeagoing && !isInitialized(addedNewSeagoing)) {
      loadNewAddedSeagoing();
    }
  }, [
    isExternal,
    isReady,
    shouldLoadSeagoing,
    shouldLoadNewSeagoing,
    addedSeagoing,
    addedNewSeagoing,
    loadAddedSeagoing,
    loadNewAddedSeagoing,
  ]);

  return result;
};

export const useAddedSeagoing = (
  group?: string
): { added: Loadable<ServiceReportExtended[]>; refresh: () => void } => {
  const { addedSeagoing, refreshSeagoing } = useAddedQualifications({
    group,
    seagoing: true,
  });
  return { added: addedSeagoing, refresh: refreshSeagoing };
};

export const useAddedNewSeagoing = (): {
  added: Loadable<ServiceReportQualificationResult>;
  refresh: () => void;
} => {
  const { addedNewSeagoing, refreshNewSeagoing } = useAddedQualifications({
    newSeagoing: true,
  });
  return { added: addedNewSeagoing, refresh: refreshNewSeagoing };
};
